import { DialogComponent } from '@theme/dialog';
import { ProductSelectEvent } from '@shopify/events';

const SUBMIT_URL = '/apps/pickall-item-request/submit';

/**
 * Opens the request modal with product/variant context.
 */
class ItemRequestLauncher extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  #onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target !== this && event.target !== this.querySelector('button')) return;
    event.preventDefault();
    this.#openDialog();
  };

  #onClick = (event) => {
    if (event.target.closest('button') === null && event.target !== this) return;
    event.preventDefault();
    this.#openDialog();
  };

  #openDialog = () => {
    const dialog = document.getElementById('item-request-dialog');
    if (!(dialog instanceof ItemRequestDialog)) return;

    dialog.openWithContext({
      productId: this.dataset.productId || '',
      productTitle: this.dataset.productTitle || '',
      variantId: this.dataset.variantId || '',
      variantTitle: this.dataset.variantTitle || '',
      productUrl:
        this.dataset.productUrl ||
        `${window.location.origin}${window.location.pathname}`,
    });
  };
}

/**
 * @typedef {object} ItemRequestDialogRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} productId
 * @property {HTMLInputElement} productTitle
 * @property {HTMLInputElement} variantId
 * @property {HTMLInputElement} variantTitle
 * @property {HTMLInputElement} productUrl
 * @property {HTMLInputElement} nameInput
 * @property {HTMLInputElement} emailInput
 * @property {HTMLInputElement} phoneInput
 * @property {HTMLTextAreaElement} messageInput
 * @property {HTMLParagraphElement} errorMessage
 * @property {HTMLParagraphElement} successMessage
 * @property {HTMLButtonElement} submitButton
 */

class ItemRequestDialog extends DialogComponent {
  /** @type {boolean} */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.form?.addEventListener('submit', this.#onSubmit);
    document.addEventListener(ProductSelectEvent.eventName, this.#onProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form?.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener(ProductSelectEvent.eventName, this.#onProductSelect);
  }

  /**
   * @param {object} ctx
   */
  openWithContext(ctx) {
    const { productId, productTitle, variantId, variantTitle, productUrl } = ctx;
    this.refs.productId.value = productId;
    this.refs.productTitle.value = productTitle;
    this.refs.variantId.value = variantId;
    this.refs.variantTitle.value = variantTitle;
    this.refs.productUrl.value = productUrl;

    this.#resetFormState();
    this.showDialog();
    queueMicrotask(() => this.refs.nameInput?.focus());
  }

  #resetFormState() {
    this.#submitting = false;
    this.refs.submitButton.disabled = false;
    this.refs.errorMessage?.classList.add('hidden');
    this.refs.successMessage?.classList.add('hidden');
    this.refs.form?.classList.remove('hidden');
    if (this.refs.nameInput) this.refs.nameInput.value = '';
    if (this.refs.emailInput) this.refs.emailInput.value = '';
    if (this.refs.phoneInput) this.refs.phoneInput.value = '';
    if (this.refs.messageInput) this.refs.messageInput.value = '';
  }

  /** @param {ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    const sourceForm = /** @type {Element | null} */ (event.target)?.closest('product-form-component');
    if (!sourceForm) return;

    try {
      const { detail } = await event.promise;
      const resource = detail?.resource;
      const productId = detail?.productId || sourceForm.dataset.productId;
      const productUrl = sourceForm.dataset.productUrl || window.location.pathname;
      const titleEl = sourceForm.closest('.product-details')?.querySelector('h1');
      const productTitle = titleEl?.textContent?.trim() || '';

      sourceForm.querySelectorAll('item-request-launcher').forEach((launcher) => {
        launcher.dataset.productId = String(productId || '');
        launcher.dataset.productTitle = productTitle;
        launcher.dataset.variantId = resource?.id ? String(resource.id) : '';
        launcher.dataset.variantTitle = resource?.title || '';
        launcher.dataset.productUrl = productUrl;
      });
    } catch {
      /* ignore stale variant events */
    }
  };

  /** @param {Event} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const { nameInput, emailInput, phoneInput, messageInput } = this.refs;
    const name = nameInput.value.trim();
    const email = emailInput.value.trim();
    const phone = phoneInput.value.trim();
    const message = messageInput.value.trim();

    this.refs.errorMessage.classList.add('hidden');

    if (!name || !email || !phone) {
      this.#showError('Please fill in all required fields.');
      return;
    }

    if (!this.#isValidEmail(email)) {
      this.#showError('Please enter a valid email address.');
      emailInput.focus();
      return;
    }

    if (!this.#isValidPhone(phone)) {
      this.#showError('Please enter a valid phone number.');
      phoneInput.focus();
      return;
    }

    this.#submitting = true;
    this.refs.submitButton.disabled = true;

    const payload = {
      customer_name: name,
      email,
      phone,
      message,
      product_id: this.refs.productId.value,
      product_title: this.refs.productTitle.value,
      variant_id: this.refs.variantId.value,
      variant_title: this.refs.variantTitle.value,
      product_url: this.refs.productUrl.value,
      requested_at: new Date().toISOString(),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Unable to submit your request. Please try again later.');
      }

      this.refs.form.classList.add('hidden');
      this.refs.successMessage.classList.remove('hidden');
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      this.#submitting = false;
      this.refs.submitButton.disabled = false;
    }
  };

  /** @param {string} message */
  #showError(message) {
    this.refs.errorMessage.textContent = message;
    this.refs.errorMessage.classList.remove('hidden');
  }

  /** @param {string} value */
  #isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  /** @param {string} value */
  #isValidPhone(value) {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }
}

if (!customElements.get('item-request-launcher')) {
  customElements.define('item-request-launcher', ItemRequestLauncher);
}

if (!customElements.get('item-request-dialog')) {
  customElements.define('item-request-dialog', ItemRequestDialog);
}
